import { WebSocketMessage, WebSocketEventHandlers } from '../types/websocket';

export class WebSocketService {
  private connection: WebSocket | null = null;
  private tenantId: string;
  private userId: number;
  private token: string;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  private eventHandlers: WebSocketEventHandlers = {};
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private isManualDisconnect: boolean = false;

  constructor(tenantId: string, userId: number, token: string) {
    this.tenantId = tenantId;
    this.userId = userId;
    this.token = token;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.connection?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      // For development with mock authentication, still attempt WebSocket connection
      // but handle authentication gracefully on the backend
      if (process.env.NODE_ENV === 'development' && this.token.startsWith('mock-jwt-token')) {
        console.log('Attempting WebSocket connection in development mode with mock authentication');
      }

      const wsUrl = `${this.getWebSocketUrl()}/api/v1/performance/ws/${this.tenantId}?user_id=${this.userId}&token=${this.token}`;
      
      try {
        this.connection = new WebSocket(wsUrl);
        this.isManualDisconnect = false;

        this.connection.onopen = () => {
          console.log('Dashboard WebSocket connected');
          this.reconnectAttempts = 0;
          this.startHeartbeat();
          this.eventHandlers.onConnect?.();
          resolve();
        };

        this.connection.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            if (message.tenantId === this.tenantId && message.user_id === this.userId) {
              this.eventHandlers.onMessage?.(message);
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.connection.onclose = (event) => {
          console.log('Dashboard WebSocket disconnected:', event.code, event.reason);
          this.stopHeartbeat();
          this.eventHandlers.onDisconnect?.();
          
          if (!this.isManualDisconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.eventHandlers.onMaxReconnectAttempts?.();
          }
        };

        this.connection.onerror = (error) => {
          console.error('Dashboard WebSocket error:', error);
          this.eventHandlers.onError?.(error);
          reject(error);
        };

      } catch (error) {
        reject(error);
      }
    });
  }

  disconnect(): void {
    this.isManualDisconnect = true;
    this.stopHeartbeat();
    
    if (this.connection) {
      this.connection.close();
      this.connection = null;
    }
  }

  sendMessage(message: Partial<WebSocketMessage>): void {
    if (this.connection?.readyState === WebSocket.OPEN) {
      const fullMessage: WebSocketMessage = {
        type: message.type || 'heartbeat',
        data: message.data || {},
        timestamp: new Date().toISOString(),
        tenantId: this.tenantId,
        user_id: this.userId,
        message_id: message.message_id || this.generateMessageId(),
        ...message
      };
      
      this.connection.send(JSON.stringify(fullMessage));
    } else {
      console.warn('WebSocket is not connected. Cannot send message.');
    }
  }

  setEventHandlers(handlers: WebSocketEventHandlers): void {
    this.eventHandlers = { ...this.eventHandlers, ...handlers };
  }

  isConnected(): boolean {
    return this.connection?.readyState === WebSocket.OPEN || false;
  }

  getConnectionStatus(): 'connecting' | 'connected' | 'disconnected' | 'error' {
    if (!this.connection) return 'disconnected';
    
    switch (this.connection.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'error';
    }
  }

  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  private getWebSocketUrl(): string {
    const local =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    // Dev on CRA :3000 → ws :8000; dev/prod UI on nginx :8080 / :8443 → same host as page
    if (process.env.NODE_ENV === 'development' && local) {
      const port = window.location.port;
      if (port === '8080' || port === '8443') {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//${window.location.host}`;
      }
      return 'ws://localhost:8000';
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      this.sendMessage({ type: 'heartbeat' });
    }, 30000); // Send heartbeat every 30 seconds
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff
    
    console.log(`Scheduling WebSocket reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);
    
    this.eventHandlers.onReconnect?.(this.reconnectAttempts);
    
    setTimeout(() => {
      if (!this.isManualDisconnect) {
        this.connect().catch(console.error);
      }
    }, delay);
  }

  private generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Factory function to create WebSocket service instances
export function createWebSocketService(tenantId: string, userId: number, token: string): WebSocketService {
  return new WebSocketService(tenantId, userId, token);
}
